// ** React Imports
import React, { forwardRef, Fragment, useState } from 'react'

// ** MUI Imports
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import Slide from '@mui/material/Slide'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import FormHelperText from '@mui/material/FormHelperText'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

// ** Icon Imports
import DeleteIcon from '@mui/icons-material/DeleteOutline'

// ** Redux Imports
import { useDispatch } from 'react-redux'
import { deleteNonGoogleSkuSubscription } from 'store/customers'

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction='up' ref={ref} {...props} />
})

const SubscriptionDelete = props => {
    const {
        subscription,
        customerId,
        triggerLabel = 'Delete',
        confirmLabel = 'Delete',
        description = 'Are you sure you want to delete this subscription? This action cannot be undone.',
        disabled = false
    } = props

    const dispatch = useDispatch()
    const [open, setOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [ISVTerminationDate, setISVTerminationDate] = useState(new Date())

    const handleOpen = () => setOpen(true)
    const handleClose = () => {
        if (!submitting) setOpen(false)
    }

    const handleConfirm = async () => {
        if (submitting) return
        try {
            setSubmitting(true)
            const data = {
                ...subscription,
                customer_id: customerId,
                endTime: ISVTerminationDate.getTime().toString(),
                is_not_google_product: false,
                is_removed: true,
              }
              dispatch(deleteNonGoogleSkuSubscription(data))
            setOpen(false)
        } catch (e) {
            // keep dialog open so user can retry or cancel
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Fragment>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Button
                    variant='text'
                    color='error'
                    onClick={handleOpen}
                    startIcon={<DeleteIcon sx={{ fontSize: '18px' }} />}
                    disabled={disabled}
                    sx={{ ml: '0.5rem', fontWeight: 600 }}
                >
                  Delete
                </Button>
                <Dialog
                    open={open}
                    keepMounted
                    onClose={handleClose}
                    TransitionComponent={Transition}
                    aria-labelledby='subscription-delete-title'
                    aria-describedby='subscription-delete-description'
                    sx={{
                        '& .MuiDialog-container': {
                            '& .MuiPaper-root': {
                                width: '100%',
                                minWidth: '480px'
                            }
                        }
                    }}
                >
                    <DialogTitle id='subscription-delete-title' sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Delete subscription</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography id='subscription-delete-description'>
                            {description}
                        </Typography>
                        <Grid item xs={12} sx={{ mt: 6, mb: 2 }}>
                            <DesktopDatePicker
                                label='ISV Termination Date'
                                inputFormat='MM/DD/YYYY'
                                value={ISVTerminationDate}
                                maxDate={new Date()}
                                onChange={(date) => {
                                    if (date) {
                                        setISVTerminationDate(date)
                                    }
                                }}
                                sx={{ width: '100%' }}
                                slotProps={{ textField: { size: 'small' } }}
                                renderInput={params => <TextField {...params} />}
                            />
                        </Grid>
                    </DialogContent>
                    <DialogActions className='dialog-actions-dense'>
                        <Button
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 600,
                                '&:hover': {
                                    backgroundColor: theme => `${theme.palette.common.white} !important`,
                                    color: 'text.secondary'
                                }
                            }}
                            onClick={handleClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            color='error'
                            sx={{
                                fontWeight: 600,
                                '&:hover': {
                                    backgroundColor: theme => theme.palette.error.dark
                                },
                                '&:disabled': {
                                    backgroundColor: theme => theme.palette.grey[700],
                                    color: 'common.white'
                                }
                            }}
                            startIcon={submitting ? <CircularProgress sx={{ color: 'common.white' }} size='1rem' /> : <DeleteIcon />}
                            onClick={handleConfirm}
                            disabled={submitting}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </LocalizationProvider>
        </Fragment>
    )
}

export default SubscriptionDelete


